import { useState, useEffect } from 'react';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import ProjectSettings from './ProjectSettings';
import { ChatMode } from '@/components/agent/AgentModeDropdown';
import { ModelType } from '@/components/agent/ModelDropdown';
import { useAuthToken } from '@/hooks/useAuthToken';
import { supabaseApi } from '@/lib/api/supabase';
import { SupabaseStatus } from '@optidev/shared-types';

interface ProjectSettingsDrawerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  initialSection?: 'info' | 'deploy' | 'optidev-cloud' | 'agent-settings';
  // Agent-related props
  mode?: ChatMode;
  model?: ModelType;
  onModeChange?: (mode: ChatMode) => void;
  onModelChange?: (model: ModelType) => void;
  isStreaming?: boolean;
}

const SECTION_TITLES = {
  info: 'Project Settings',
  deploy: 'Deploy History',
  'optidev-cloud': 'OptiDev Cloud',
  'agent-settings': 'Agent Settings',
};

const getStatusColor = (status: SupabaseStatus | null) => {
  switch (status) {
    case SupabaseStatus.ACTIVE_HEALTHY:
      return 'bg-emerald-500';
    case SupabaseStatus.PROVISIONING:
    case SupabaseStatus.RESTORING:
      return 'bg-blue-500 animate-pulse';
    case SupabaseStatus.PAUSED:
      return 'bg-amber-500';
    case SupabaseStatus.FAILED:
      return 'bg-red-500';
    default:
      return 'bg-zinc-400';
  }
};

const getStatusLabel = (status: SupabaseStatus | null) => {
  switch (status) {
    case SupabaseStatus.ACTIVE_HEALTHY:
      return 'Active';
    case SupabaseStatus.PROVISIONING:
      return 'Provisioning';
    case SupabaseStatus.RESTORING:
      return 'Restoring';
    case SupabaseStatus.PAUSED:
      return 'Paused';
    case SupabaseStatus.FAILED:
      return 'Failed';
    default:
      return '';
  }
};

export function ProjectSettingsDrawerSheet({
  open,
  onOpenChange,
  projectId,
  projectName,
  initialSection = 'info',
  mode,
  model,
  onModeChange,
  onModelChange,
  isStreaming,
}: ProjectSettingsDrawerSheetProps): JSX.Element {
  const { token } = useAuthToken();
  const [cloudStatus, setCloudStatus] = useState<SupabaseStatus | null>(null);

  // Fetch OptiDev Cloud status when opening the cloud section
  useEffect(() => {
    if (open && initialSection === 'optidev-cloud' && token && projectId) {
      supabaseApi.getProjectStatus(token, projectId)
        .then((data) => setCloudStatus(data.status))
        .catch(() => setCloudStatus(null));
    }
  }, [open, initialSection, token, projectId]);

  // Status badge for OptiDev Cloud
  const statusBadge = initialSection === 'optidev-cloud' && cloudStatus ? (
    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted/50">
      <div className={`w-1.5 h-1.5 rounded-full ${getStatusColor(cloudStatus)}`} />
      <span className="text-xs font-medium text-muted-foreground">{getStatusLabel(cloudStatus)}</span>
    </div>
  ) : undefined;

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={SECTION_TITLES[initialSection]}
      titleSuffix={statusBadge}
    >
      <ProjectSettings
        projectId={projectId}
        projectName={projectName}
        initialSection={initialSection}
        onClose={() => onOpenChange(false)}
        viewMode="settings"
        mode={mode}
        model={model}
        onModeChange={onModeChange}
        onModelChange={onModelChange}
        isStreaming={isStreaming}
        hideBottomNav
      />
    </BottomSheet>
  );
}
